type DefaultCache = object;
type EventFn = (ev: StorageEvent) => void;
type EventListener<T = DefaultCache> = (
  key: string,
  tokens: T | null
) => unknown;

export interface AuthStorageBuilder<T extends DefaultCache = DefaultCache> {
  addEventListener(listener: EventListener<T>): void;
  removeEventListener(): void;
  get(key: string): Promise<T | null>;
  set(key: string, tokens: T): Promise<void>;
  remove(key: string): Promise<void>;
}

export type StorageEvents = {
  addEventListener(type: 'storage', listener: EventFn): void;
  removeEventListener(type: 'storage', listener: EventFn): void;
  dispatchEvent(event: StorageEvent): boolean;
};

interface AuthCacheManagerOptions {
  storage: Storage;
  events: StorageEvents;
}

const DEFAULT_OPTIONS: AuthCacheManagerOptions = {
  storage: localStorage,
  events: window,
};

export class AuthStorageService<T extends DefaultCache = DefaultCache>
  implements AuthStorageBuilder<T>
{
  private currentCache: T | null = null;
  private listenerFn: EventFn | null = null;

  constructor(private options = DEFAULT_OPTIONS) {}

  private get storage() {
    return this.options.storage;
  }

  private get events() {
    return this.options.events;
  }

  private get currentCacheString() {
    return this.currentCache ? JSON.stringify(this.currentCache) : null;
  }

  addEventListener(listener: EventListener<T>): void {
    this.listenerFn = this.buildEventListener(listener);
    this.events.addEventListener('storage', this.listenerFn);
  }

  removeEventListener(): void {
    if (this.listenerFn) {
      this.events.removeEventListener('storage', this.listenerFn);
      this.listenerFn = null;
    }
  }

  get(key: string) {
    return this.wrapPromise(() => {
      const cachedTokens = this.storage.getItem(key);
      return cachedTokens ? JSON.parse(cachedTokens) : null;
    });
  }

  set(key: string, tokens: T) {
    return this.wrapPromise(() => {
      const tokensString = JSON.stringify(tokens);
      this.storage.setItem(key, tokensString);
      this.dispatchEvent(key, tokensString);
      this.currentCache = tokens;
    });
  }

  remove(key: string) {
    return this.wrapPromise(() => {
      this.storage.removeItem(key);
      this.dispatchEvent(key, null);
      this.currentCache = null;
    });
  }

  private dispatchEvent(key: string, newValue: string | null) {
    this.events.dispatchEvent(
      new StorageEvent('storage', {
        key,
        storageArea: this.storage,
        newValue,
      })
    );
  }

  private wrapPromise<R>(fn: () => R): Promise<R> {
    return new Promise<R>((resolve) => resolve(fn()));
  }

  private buildEventListener(listener: EventListener<T>) {
    return (event: StorageEvent) => {
      if (
        event.key &&
        event.storageArea === this.storage &&
        event.newValue !== this.currentCacheString &&
        (event.newValue === null || event.newValue?.startsWith('{'))
      ) {
        listener(event.key, event.newValue ? JSON.parse(event.newValue) : null);
      }
    };
  }
}
